import { LoadingPage } from '@/components/loading-page';
import { Button } from '@/components/ui/button';
import { RegisteredToOptions } from '@/lib/routing';
import { Link } from '@tanstack/react-router';
import { Ban } from 'lucide-react';
import { ReactNode, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

export interface GeneralErrorBoundaryProps {
  children: ReactNode;
  onError: (error: unknown) => {
    errorHeader: ReactNode;
    errorSubHeader?: ReactNode;
    allowRetry?: boolean;
    back?: {
      label?: ReactNode;
      to: RegisteredToOptions;
    };
  };
}

export const GeneralErrorBoundary = ({ children, onError }: GeneralErrorBoundaryProps) => {
  return (
    <ErrorBoundary
      fallbackRender={({ error, resetErrorBoundary }) => {
        const { errorHeader, errorSubHeader, allowRetry, back } = onError(error);
        return (
          <ErrorInfoPage
            errorHeader={errorHeader}
            errorSubHeader={errorSubHeader}
            retry={allowRetry ? resetErrorBoundary : undefined}
            back={back}
          />
        );
      }}
    >
      <Suspense fallback={<LoadingPage />}>{children}</Suspense>
    </ErrorBoundary>
  );
};

interface ErrorInfoPageProps {
  errorHeader: ReactNode;
  errorSubHeader?: ReactNode;
  retry?: () => void;
  back?: {
    label?: ReactNode;
    to: RegisteredToOptions;
  };
}

const ErrorInfoPage = ({ errorHeader, errorSubHeader, retry, back }: ErrorInfoPageProps) => {
  return (
    <div className="flex justify-center items-center h-3/4 p-4 flex-col gap-2">
      <div className="flex items-center justify-center gap-2">
        <Ban />
        <h2 className="items-center flex font-bold ">{errorHeader}</h2>
      </div>
      <h4>{errorSubHeader}</h4>
      <div className="flex justify-center gap-2 w-[150px]">
        {retry && <Button onClick={retry}>Retry</Button>}

        {back && (
          <Link {...back.to}>
            <Button variant="outline">{back.label ?? 'Back'}</Button>
          </Link>
        )}
      </div>
    </div>
  );
};
